import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { useToast, useToastError } from "@/components/ui/use-toast";
import { useApi } from "@/hooks/useApi";
import { useDeviceFormat } from "@/hooks/useDeviceFormat";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import {
  AdminTeamDTO,
  AdminUserDTO,
  CompanyDTO,
  Nullable,
  SubscriptionType,
} from "@shaarp/common";
import { format } from "date-fns";
import { enUS, fr } from "date-fns/locale";
import { CalendarIcon, RotateCcwIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ButtonLoading } from "../button-loading";
import CompanySelector from "../company/company-selector";
import { Button } from "../ui/button";
import { Calendar } from "../ui/calendar";
import { DialogFooter, DialogHeader } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { UserSelector } from "./user-selector";

type TeamPreset = {
  name: string;
  enabled?: boolean;
  setupRequired?: boolean;
  billingAndPaymentMethodRequired?: boolean;
  trial?: boolean;
  trialCountDays?: number;
  trialEndDate?: Date | null;
  discount?: boolean;
  monthlyDiscount?: string;
  annualDiscount?: string;
  initialProLicenseFrequency?: SubscriptionType;
  initialProLicenseCount?: number;
};

export const DialogAdminTeamCreation = ({
  open,
  onCreated = () => {},
  onClose = () => {},
}: {
  open: boolean;
  onCreated?: (team: AdminTeamDTO) => void;
  onClose?: () => void;
}) => {
  const { toast } = useToast();
  const toastError = useToastError();
  const api = useApi();
  const { t, currentLanguage } = useTranslation();
  const { isMobile } = useDeviceFormat();
  const { companyId } = useParams();

  const [loading, setLoading] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [setupRequired, setSetupRequired] = useState(true);
  const [billingAndPaymentMethodRequired, setBillingAndPaymentMethodRequired] =
    useState(true);
  const [trial, setTrial] = useState<boolean>(false);
  const [trialCountDays, setTrialCountDays] = useState<number>(7);
  const [name, setName] = useState("");
  const [managerEmail, setManagerEmail] = useState("");
  const [companySelected, setCompanySelected] =
    useState<Nullable<CompanyDTO>>(null);
  const [discount, setDiscount] = useState<boolean>(false);
  const [trialEndDate, setTrialEndDate] = useState<Date | null>(null);
  const [monthlyDiscount, setMonthlyDiscount] = useState<string>("");
  const [annualDiscount, setAnnualDiscount] = useState<string>("");
  const [presetName, setPresetName] = useState<string>("");
  const [initialProLicenseFrequency, setInitialProLicenseFrequency] =
    useState<SubscriptionType>("monthly");
  const [initialProLicenseCount, setInitialProLicenseCount] =
    useState<number>(1);
  const [referralEnabled, setReferralEnabled] = useState<boolean>(false);
  const [referralUserSelected, setReferralUserSelected] =
    useState<Nullable<AdminUserDTO>>(null);
  const [advisorEnabled, setAdvisorEnabled] = useState<boolean>(false);
  const [advisorSelected, setAdvisorSelected] =
    useState<Nullable<AdminUserDTO>>(null);

  const presets: TeamPreset[] = [
    {
      name: "Bras Droits Des Dirigeants",
      enabled: true,
      setupRequired: true,
      trial: false,
      trialCountDays: 0,
      trialEndDate: null,
      discount: true,
      monthlyDiscount: "bdddmonthly",
      annualDiscount: "bdddannually",
      initialProLicenseFrequency: "monthly",
      initialProLicenseCount: 1,
      billingAndPaymentMethodRequired: true,
    },
    {
      name: "Rivalis",
      enabled: true,
      setupRequired: true,
      trial: false,
      trialCountDays: 0,
      trialEndDate: null,
      discount: true,
      monthlyDiscount: "rivalismonthly",
      annualDiscount: "rivalisyearly",
      initialProLicenseFrequency: "monthly",
      initialProLicenseCount: 1,
      billingAndPaymentMethodRequired: true,
    },
    {
      name: "Kairos",
      enabled: true,
      setupRequired: true,
      trial: false,
      trialCountDays: 0,
      trialEndDate: null,
      discount: true,
      monthlyDiscount: "kairosmonthly",
      annualDiscount: "kairosyearly",
      initialProLicenseFrequency: "monthly",
      initialProLicenseCount: 1,
      billingAndPaymentMethodRequired: true,
    },
  ];

  useEffect(() => {
    if (companyId) {
      const fetchCompany = async () => {
        const { data } = await api.get<CompanyDTO>(
          `/company/available/${companyId}`
        );
        setCompanySelected(data);
      };
      fetchCompany();
    }
  }, [companyId]);

  const setDefaultPreset = () => {
    const defaultPreset: TeamPreset = {
      name: "Default",
      enabled: true,
      setupRequired: true,
      trial: false,
      trialCountDays: 0,
      trialEndDate: null,
      discount: false,
      monthlyDiscount: "",
      annualDiscount: "",
      initialProLicenseFrequency: "monthly",
      initialProLicenseCount: 1,
      billingAndPaymentMethodRequired: true,
    };

    setPreset(defaultPreset);
  };

  // Free teams (billing disabled) only get monthly Pro licenses, so the annual
  // frequency is unavailable — force monthly whenever billing is turned off.
  const handleBillingRequiredChange = (checked: boolean) => {
    setBillingAndPaymentMethodRequired(checked);
    if (!checked) {
      setInitialProLicenseFrequency("monthly");
    }
  };

  const handlePresetChange = (newPresetName: string) => {
    setPresetName(newPresetName);

    const newPreset = presets.find((preset) => preset.name === newPresetName);
    if (newPreset) {
      setPreset(newPreset);
    }
  };

  const setPreset = (newPreset: TeamPreset) => {
    if (newPreset.enabled !== undefined) {
      setEnabled(newPreset.enabled);
    }

    if (newPreset.setupRequired !== undefined) {
      setSetupRequired(newPreset.setupRequired);
    }

    if (newPreset.billingAndPaymentMethodRequired !== undefined) {
      setBillingAndPaymentMethodRequired(
        newPreset.billingAndPaymentMethodRequired
      );
    }

    if (newPreset.trial !== undefined) {
      setTrial(newPreset.trial);
    }

    if (newPreset.trialCountDays !== undefined) {
      setTrialCountDays(newPreset.trialCountDays);
    }

    if (newPreset.trialEndDate !== undefined) {
      setTrialEndDate(newPreset.trialEndDate);
      if (newPreset.trialEndDate) {
        setTrial(true);
        setTrialCountDays(0);
      }
    }

    if (newPreset.discount !== undefined) {
      setDiscount(newPreset.discount);
    }

    if (newPreset.monthlyDiscount !== undefined) {
      setMonthlyDiscount(newPreset.monthlyDiscount);
      if (newPreset.monthlyDiscount) {
        setDiscount(true);
      }
    }

    if (newPreset.annualDiscount !== undefined) {
      setAnnualDiscount(newPreset.annualDiscount);
      if (newPreset.annualDiscount) {
        setDiscount(true);
      }
    }

    if (
      !newPreset.monthlyDiscount &&
      !newPreset.annualDiscount &&
      newPreset.discount === false
    ) {
      setDiscount(false);
    }

    if (newPreset.initialProLicenseFrequency !== undefined) {
      setInitialProLicenseFrequency(newPreset.initialProLicenseFrequency);
    }

    if (newPreset.initialProLicenseCount !== undefined) {
      setInitialProLicenseCount(newPreset.initialProLicenseCount);
    }
  };

  const createNewTeam = async () => {
    if (name === "") {
      toastError();
      return;
    }

    setLoading(true);
    try {
      const { data } = await api.post<AdminTeamDTO>(`/admin/team`, {
        status: enabled ? "enabled" : "disabled",
        setupRequired,
        name,
        billingAndPaymentMethodRequired,
        managerEmail,
        companyId: companySelected?.id || null,
        trialCountDays:
          trial === true && trialEndDate === null ? trialCountDays : null,
        trialEndDate:
          trial === true && trialEndDate !== null ? trialEndDate : null,
        monthlyDiscount: discount === true ? monthlyDiscount : null,
        annualDiscount: discount === true ? annualDiscount : null,
        initialProLicenseCountMonthly:
          initialProLicenseFrequency === "monthly"
            ? initialProLicenseCount
            : null,
        initialProLicenseCountAnnual:
          initialProLicenseFrequency === "annual"
            ? initialProLicenseCount
            : null,
        referralUserId:
          referralEnabled === true ? referralUserSelected?.id : null,
        advisorUserId: advisorEnabled === true ? advisorSelected?.id : null,
      });

      toast({
        variant: "default",
        title: t("admin.team.creation.toast.success.title"),
        description: t("admin.team.creation.toast.success.description", {
          name: data.name,
        }),
      });

      onCreated(data);
    } catch (error) {
      toastError();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="w-full max-w-[700px]">
        <DialogHeader>
          <DialogTitle>{t("admin.team.creation.title")}</DialogTitle>
        </DialogHeader>
        <div>
          <div className="grid grid-cols-1 gap-4">
            <div className="flex flex-row gap-2 items-center w-full">
              <Select value={presetName} onValueChange={handlePresetChange}>
                <SelectTrigger className="w-full h-9">
                  <SelectValue
                    placeholder={t(
                      "admin.team.creation.fields.preset.placeholder"
                    )}
                  />
                </SelectTrigger>
                <SelectContent side="top">
                  {presets.map((preset) => (
                    <SelectItem key={preset.name} value={`${preset.name}`}>
                      {preset.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" size="icon" onClick={setDefaultPreset}>
                <RotateCcwIcon className="w-4 h-4" />
              </Button>
            </div>
            <div className="space-y-2">
              <Label htmlFor="name">
                {t("admin.team.creation.fields.enabled")}
                <Switch
                  checked={enabled}
                  onCheckedChange={setEnabled}
                  className="ml-2 align-middle"
                />
              </Label>
            </div>
            <div className="space-y-2">
              <Label htmlFor="name">
                {t("admin.team.creation.fields.name.label")} *
              </Label>
              <Input
                id="name"
                placeholder={t("admin.team.creation.fields.name.placeholder")}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="name">
                {t("admin.team.creation.fields.setupRequired")}
                <Switch
                  checked={setupRequired}
                  onCheckedChange={setSetupRequired}
                  className="ml-2 align-middle"
                />
              </Label>
            </div>
            <div className="space-y-2">
              <Label htmlFor="name">
                {t(
                  "admin.team.creation.fields.billingAndPaymentMethodRequired"
                )}
                <Switch
                  checked={billingAndPaymentMethodRequired}
                  onCheckedChange={handleBillingRequiredChange}
                  className="ml-2 align-middle"
                />
              </Label>
            </div>
            <div className="space-y-2">
              <Label htmlFor="name">
                {t("admin.team.creation.fields.trial.label")}
                <Switch
                  checked={trial}
                  onCheckedChange={setTrial}
                  className="ml-2 align-middle"
                />
              </Label>
              {trial === true && (
                <div className="flex flex-row gap-2">
                  <div className="flex flex-1 gap-2">
                    <div className="flex flex-col flex-1">
                      <Label
                        htmlFor="trialCountDays"
                        className="mb-1 ml-1 text-xs font-medium text-muted-foreground"
                      >
                        {t("admin.team.creation.fields.trial.trialDays")}
                      </Label>
                      <Input
                        id="trialCountDays"
                        type="number"
                        value={trialCountDays}
                        onChange={(e) => {
                          setTrialCountDays(Number(e.target.value));
                          setTrialEndDate(null);
                        }}
                        min={1}
                        max={365}
                        className="text-sm"
                      />
                    </div>
                    <div className="flex flex-col flex-1">
                      <Label
                        htmlFor="trialEndDate"
                        className="mb-1 ml-1 text-xs font-medium text-muted-foreground"
                      >
                        {t("admin.team.creation.fields.trial.trialEndDate")}
                      </Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant={"outline"}
                            className={cn(
                              "w-full justify-start text-left font-normal text-sm",
                              !trialEndDate && "text-muted-foreground"
                            )}
                          >
                            <CalendarIcon className="mr-2 w-4 h-4" />
                            {trialEndDate ? (
                              format(trialEndDate, "PPP", {
                                locale: currentLanguage === "fr" ? fr : enUS,
                              })
                            ) : (
                              <span>
                                {t(
                                  "admin.dataTableAdminTeams.dialogs.trial.selectDate"
                                )}
                              </span>
                            )}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="p-0 w-auto">
                          <Calendar
                            mode="single"
                            selected={trialEndDate || undefined}
                            onSelect={(date) => {
                              setTrialEndDate(date || null);
                              setTrialCountDays(0);
                            }}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="name">
                {t("admin.team.creation.fields.discount")}
                <Switch
                  checked={discount}
                  onCheckedChange={setDiscount}
                  className="ml-2 align-middle"
                />
              </Label>
              {discount === true && (
                <div className="flex flex-row gap-2">
                  <div className="flex flex-1 gap-2">
                    <div className="flex flex-col flex-1">
                      <Label
                        htmlFor="monthlyDiscount"
                        className="mb-1 ml-1 text-xs font-medium text-muted-foreground"
                      >
                        {t("admin.team.creation.fields.monthlyDiscount.label")}
                      </Label>
                      <Input
                        id="monthlyDiscount"
                        placeholder={t(
                          "admin.team.creation.fields.monthlyDiscount.placeholder"
                        )}
                        type="text"
                        value={monthlyDiscount}
                        onChange={(e) => setMonthlyDiscount(e.target.value)}
                        className="text-sm"
                      />
                    </div>
                    <div className="flex flex-col flex-1">
                      <Label
                        htmlFor="annualDiscount"
                        className="mb-1 ml-1 text-xs font-medium text-muted-foreground"
                      >
                        {t("admin.team.creation.fields.annualDiscount.label")}
                      </Label>
                      <Input
                        id="annualDiscount"
                        placeholder={t(
                          "admin.team.creation.fields.annualDiscount.placeholder"
                        )}
                        type="text"
                        value={annualDiscount}
                        onChange={(e) => setAnnualDiscount(e.target.value)}
                        className="text-sm"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="initialProLicenseCount">
                {t("admin.team.creation.fields.initialProLicenseCount.label")}
              </Label>
              <div className="flex flex-col gap-2">
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={
                      initialProLicenseFrequency === "monthly"
                        ? "default"
                        : "outline"
                    }
                    size="sm"
                    className="flex-1"
                    onClick={() => setInitialProLicenseFrequency("monthly")}
                  >
                    {t(
                      "admin.team.creation.fields.initialProLicenseCount.monthly"
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant={
                      initialProLicenseFrequency === "annual"
                        ? "default"
                        : "outline"
                    }
                    size="sm"
                    className="flex-1"
                    disabled={!billingAndPaymentMethodRequired}
                    onClick={() => setInitialProLicenseFrequency("annual")}
                  >
                    {t(
                      "admin.team.creation.fields.initialProLicenseCount.annual"
                    )}
                  </Button>
                </div>
                <Input
                  id="initialProLicenseCount"
                  type="number"
                  min={1}
                  value={initialProLicenseCount}
                  onChange={(e) =>
                    setInitialProLicenseCount(Number(e.target.value))
                  }
                />
                <p className="ml-1 text-xs text-muted-foreground">
                  {t(
                    "admin.team.creation.fields.initialProLicenseCount.helper"
                  )}
                </p>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="teamReferral">
                {t("admin.team.creation.fields.referral.label")}
                <Switch
                  checked={referralEnabled}
                  onCheckedChange={setReferralEnabled}
                  className="ml-2 align-middle"
                />
              </Label>
              {referralEnabled === true && (
                <UserSelector
                  onSelect={(user) => setReferralUserSelected(user)}
                />
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="teamAdvisor">
                {t("admin.team.creation.fields.advisor.label")}
                <Switch
                  checked={advisorEnabled}
                  onCheckedChange={setAdvisorEnabled}
                  className="ml-2 align-middle"
                />
              </Label>
              {advisorEnabled === true && (
                <UserSelector
                  filter={(user) => !!user.advisorLicence}
                  onSelect={(user) => setAdvisorSelected(user)}
                />
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="name">
                {t("admin.team.creation.fields.managerEmail.label")}
              </Label>
              <Input
                id="managerEmail"
                placeholder={t(
                  "admin.team.creation.fields.managerEmail.placeholder"
                )}
                type="email"
                value={managerEmail}
                onChange={(e) => setManagerEmail(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="name">
                {t("admin.team.creation.fields.company")}
              </Label>
              <CompanySelector
                className="w-full"
                company={companySelected}
                onChange={setCompanySelected}
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button
            className={cn(isMobile ? "mt-2" : "")}
            variant="outline"
            onClick={onClose}
            disabled={loading}
          >
            {t("admin.team.creation.actions.cancel")}
          </Button>
          <ButtonLoading onClick={createNewTeam} isLoading={loading}>
            {t("admin.team.creation.actions.continue")}
          </ButtonLoading>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
